// T-29 — catalog open / start-tour actions for Ask Firefly controller.

import { useCallback, type Dispatch, type SetStateAction } from "react";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { canAccessPrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { AuthContextValue } from "@/contexts/AuthContext";
import type { ActivityDockAssistLevel } from "@/lib/activityDock/assistLevel";
import type { HasCapabilityFn } from "@/lib/capabilities/capabilities";
import {
  buildFireflyCatalogOpenEventDetail,
  emitFireflyCatalogOpenEvent,
} from "@/lib/firefly/fireflyCatalogExplore";
import {
  listAccessibleFireflyTours,
  type AccessibleFireflyTour,
} from "@/lib/firefly/listAccessibleFireflyTours";
import {
  buildFireflyTourRequestEventDetail,
  emitFireflyTourRequestEvent,
} from "@/lib/firefly/fireflyTourRequestEvent";
import { FIREFLY_TOUR_CATALOG } from "@/lib/firefly/tourCatalog";
import type { FireflyTourArtifact, FireflyTourTrigger } from "@/lib/firefly/tourTypes";

export type FireflyAskCatalogUiPatch =
  | { kind: "catalog"; entries: AccessibleFireflyTour[] }
  | { kind: "tour"; message: string }
  | { kind: "idle" };

export function useFireflyAskCatalog(input: {
  auth: AuthContextValue;
  currentMode: PrimaryMode | null;
  disabled: boolean;
  assistLevel: ActivityDockAssistLevel;
  hasCapability: HasCapabilityFn;
  onCloseDock?: () => void;
  onStartTour?: (artifact: FireflyTourArtifact, trigger?: FireflyTourTrigger) => void;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  applyCatalogUi: (next: FireflyAskCatalogUiPatch) => void;
}) {
  const {
    auth,
    currentMode,
    disabled,
    assistLevel,
    hasCapability,
    onCloseDock,
    onStartTour,
    setExpanded,
    applyCatalogUi,
  } = input;

  const openCatalog = useCallback(
    (source: "chip" | "ask") => {
      if (disabled || currentMode == null) return;
      const entries = listAccessibleFireflyTours({
        artifacts: FIREFLY_TOUR_CATALOG,
        auth,
        currentMode,
        canAccessPrimaryMode,
        hasCapability,
      });
      emitFireflyCatalogOpenEvent(
        buildFireflyCatalogOpenEventDetail({
          source,
          mode: currentMode,
          tourCount: entries.length,
          nowMs: Date.now(),
        }),
      );
      setExpanded(true);
      applyCatalogUi({ kind: "catalog", entries });
    },
    [applyCatalogUi, auth, currentMode, disabled, hasCapability, setExpanded],
  );

  const startTourFromCatalog = useCallback(
    (artifact: FireflyTourArtifact) => {
      if (currentMode == null) return;
      emitFireflyTourRequestEvent(
        buildFireflyTourRequestEventDetail({
          tourId: artifact.id,
          trigger: "catalog",
          intent: artifact.defaultIntent,
          mode: currentMode,
          targetMode: artifact.steps[0]?.target.mode,
          outcome: "started",
          assistLevel,
          nowMs: Date.now(),
          inputModality: "text",
        }),
      );
      onCloseDock?.();
      onStartTour?.(artifact, "catalog");
      applyCatalogUi({
        kind: "tour",
        message: `Starting ${artifact.id.replace(/^tour-/, "").replace(/-/g, " ")} tour — follow the highlights. Dismiss anytime.`,
      });
    },
    [applyCatalogUi, assistLevel, currentMode, onCloseDock, onStartTour],
  );

  const closeCatalog = useCallback(() => {
    applyCatalogUi({ kind: "idle" });
  }, [applyCatalogUi]);

  const openCatalogFromAskQuery = useCallback(() => {
    if (currentMode == null) return;
    openCatalog("ask");
  }, [currentMode, openCatalog]);

  return { openCatalog, startTourFromCatalog, closeCatalog, openCatalogFromAskQuery };
}
